import { useEffect, useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { getVapidPublicKey, subscribePush, unsubscribePush } from '../api/push';
import { Button } from './ui/button';
import { detectIos, detectStandalone } from '@/lib/platform';

type Status = 'checking' | 'unsupported' | 'off' | 'on' | 'needs-install';

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export default function PushNotificationToggle() {
  const [status, setStatus] = useState<Status>('checking');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        if (!cancelled) setStatus('unsupported');
        return;
      }
      if (detectIos() && !detectStandalone()) {
        if (!cancelled) setStatus('needs-install');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!cancelled) setStatus(subscription ? 'on' : 'off');
    }

    detect().catch(() => {
      if (!cancelled) setStatus('unsupported');
    });

    return () => {
      cancelled = true;
    };
  }, []);

  async function enable() {
    setBusy(true);
    setError('');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError('Notifications were not allowed. You can turn this on again from your browser settings.');
        setBusy(false);
        return;
      }

      const keyRes = await getVapidPublicKey();
      if (!keyRes.data?.publicKey) {
        setError('Push notifications are not configured yet.');
        setBusy(false);
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRes.data.publicKey),
      });

      const subscribeRes = await subscribePush(subscription.toJSON() as PushSubscriptionJSON);
      if (subscribeRes.error) {
        await subscription.unsubscribe();
        setError(subscribeRes.error);
        setBusy(false);
        return;
      }

      setStatus('on');
    } catch {
      setError('Could not turn on notifications on this device.');
    }
    setBusy(false);
  }

  async function disable() {
    setBusy(true);
    setError('');
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await unsubscribePush(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setStatus('off');
    } catch {
      setError('Could not turn off notifications on this device.');
    }
    setBusy(false);
  }

  if (status === 'checking' || status === 'unsupported') return null;

  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <div className="flex items-start gap-2.5">
        {status === 'on' ? (
          <Bell className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        ) : (
          <BellOff className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div>
          <p className="text-sm font-medium text-foreground">Phone notifications</p>
          {status === 'needs-install' ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Add hrma to your home screen first, then come back here to turn this on.
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Get notified on this device when there is a new notification.
            </p>
          )}
          {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
        </div>
      </div>

      {status !== 'needs-install' && (
        <Button
          variant={status === 'on' ? 'outline' : 'default'}
          size="sm"
          disabled={busy}
          onClick={status === 'on' ? disable : enable}
        >
          {busy ? '...' : status === 'on' ? 'Turn off' : 'Turn on'}
        </Button>
      )}
    </div>
  );
}
